<script>
import LogisticsDefaultSuggestions from "@/components/fulfillment/LogisticsDefaultSuggestions.vue"
import LogisticsDefaults from "@/utils/logisticsDefaults"
import SpecTypeForm from '@/components/fulfillment/SpecTypeForm.vue'

export default {
  name: 'PackingSkuDialog',
  components: { SpecTypeForm, LogisticsDefaultSuggestions },
  emits: ['active', 'queue-scan'],
  data() {
    return {
      open: false, busy: false, target: null, result: null, issue: null,
      stage: 'CHECK', error: '', form: {}, partnerSkuCode: '',
      brands: [], categories: [], packUnits: [], referenceLoaded: false,
      pending: null, changed: false, retryPayload: null, queuedBarcode: ''
    }
  },
  computed: {
    title() {
      return { CHECK: '상품 기준정보 확인', CONFIRM: '미등록 상품 확인', EDIT: '상품 등록·보완', LINK: '기존 상품 연결' }[this.stage]
    },
    canSave() { return this.result?.canSave === true },
    isLink() { return this.issue?.status === 'LINK_REQUIRED' },
    specTypeOptions() {
      return [{ label: '상품 옵션', value: 'PRODUCT_OPTION' }, { label: '의류 사이즈', value: 'APPAREL_SIZE' }, { label: '입수량', value: 'PACK_COUNT' }]
    }
  },
  methods: {
    applyDimensionDefaults(values) {
      Object.assign(values, LogisticsDefaults.applySuggestions(values))
    },
    onLogisticsValueInput(values, key) {
      Object.assign(values, LogisticsDefaults.clearAcceptance(values, key))
    },
    logisticsDefaultMetadata(values) {
      if (!Array.isArray(values.dimension_default_fields)) return {}
      return { dimension_default_fields: LogisticsDefaults.acceptedPayload(values).dimension_default_fields || [] }
    },

    queueNextScan() {
      this.queuedBarcode = ''
      this.$emit('queue-scan')
    },
    async api(path, data) {
      const res = await this.$http({ method: 'POST', url: `/api/fulfillment/packing/${path}`, data, timeout: 15000, skipSpinner: true })
      if (res?.data?.ok !== true) throw Object.assign(new Error('응답을 확인할 수 없습니다. 다시 조회해 주세요.'), { response: res })
      return res.data.data
    },
    ensure(target) {
      if (this.pending) return Promise.resolve({ ready: false, changed: false })
      this.target = { ...target }
      this.result = null
      this.issue = null
      this.changed = false
      this.retryPayload = null
      this.queuedBarcode = ''
      this.$emit('active', true)
      return new Promise(resolve => {
        this.pending = resolve
        this.check()
      })
    },
    async check() {
      if (this.busy) return
      this.busy = true
      this.stage = 'CHECK'
      this.error = ''
      this.retryPayload = null
      try {
        const result = await this.api('sku-check', this.target)
        if (!Array.isArray(result?.products) || !result.products.length) throw new Error('상품 조회 결과를 확인할 수 없습니다.')
        this.result = result
        if (result.ready && result.products.every(item => item.status === 'READY')) {
          this.finish(true)
          return
        }
        this.issue = result.products.find(item => item.status !== 'READY')
        const product = this.issue.product || {}
        const spec = product.spec_detail || {}
        this.form = {
          ...product, display_name: product.display_name || this.issue.orderItemName || '',
          barcode: this.issue.barcode, brand_id: product.brand_id || '', category_id: product.category_id || '',
          hs_code: product.hs_code || '', spec_type: product.spec_type || 'PRODUCT_OPTION',
          spec_detail: { ...spec, apparel_size: spec.size || '', pack_unit_type_code: spec.unit || '', pack_count: spec.count || '' }
        }
        this.partnerSkuCode = this.issue.partnerSkuCode || this.issue.barcode
        this.stage = this.issue.status === 'MISSING' ? 'CONFIRM' : this.isLink ? 'LINK' : 'EDIT'
        if (this.issue.status === 'BLOCKED') this.error = '비활성 또는 충돌하는 상품 연결입니다. 관리자 상품 관리에서 확인해 주세요.'
        this.open = true
        if (this.canSave && this.stage === 'EDIT') await this.loadReferences()
      } catch (e) {
        this.open = true
        this.stage = 'CHECK'
        this.error = this.errorMessage(e, '조회에 실패했습니다. 미등록 상품으로 판단하지 않습니다. 다시 조회해 주세요.')
      } finally { this.busy = false }
    },
    errorMessage(e, fallback) {
      return e?.response?.data?.message || fallback
    },
    async loadReferences() {
      if (this.referenceLoaded) return
      const values = await Promise.all(['brands', 'categories', 'pack-unit-types'].map(async name => {
        const res = await this.$http({ method: 'GET', url: `/api/fulfillment/${name}`, timeout: 15000, skipSpinner: true })
        if (res?.data?.ok !== true || !Array.isArray(res.data.data)) throw new Error('기준정보 조회 실패')
        return res.data.data
      }))
      ;[this.brands, this.categories, this.packUnits] = values
      this.referenceLoaded = true
    },
    async confirmRegistration() {
      if (this.busy) return
      // Confirmation opens the form only. No mutation API is called here.
      this.stage = 'EDIT'
      this.busy = true
      this.error = ''
      try { await this.loadReferences() }
      catch (e) { this.error = '등록 기준정보를 불러오지 못했습니다. 다시 조회해 주세요.' }
      finally { this.busy = false }
    },
    buildPayload() {
      const detail = this.form.spec_detail || {}
      const product = { ...this.form, ...this.logisticsDefaultMetadata(this.form), spec_detail: {
        model_code: detail.model_code, color: detail.color,
        size: this.form.spec_type === 'APPAREL_SIZE' ? detail.apparel_size : detail.size,
        unit: detail.pack_unit_type_code, count: detail.pack_count
      } }
      return { ...this.target, barcode: this.issue.barcode, masterSkuId: this.issue.masterSkuId,
        snapshot: this.issue.snapshot, partnerSkuCode: this.partnerSkuCode,
        action: this.isLink ? 'LINK' : this.issue.status === 'MISSING' ? 'CREATE' : 'COMPLETE', product }
    },
    validateForm() {
      if (!String(this.partnerSkuCode || '').trim()) return '파트너 상품 코드를 입력해 주세요.'
      if (this.isLink) return ''
      if (!String(this.form.display_name || '').trim()) return '상품명을 입력해 주세요.'
      if (!this.form.brand_id || !this.form.category_id) return '브랜드와 카테고리를 선택해 주세요.'
      if (!/^\d{6}$/.test(String(this.form.hs_code || '').trim())) return 'HS CODE는 숫자 6자리로 입력해 주세요.'
      const detail = this.form.spec_detail || {}
      if (this.form.spec_type === 'PRODUCT_OPTION' && ['model_code', 'color', 'size'].some(key => !String(detail[key] || '').trim())) return '모델코드·색상·사이즈를 모두 입력해 주세요.'
      if (this.form.spec_type === 'APPAREL_SIZE' && !String(detail.apparel_size || '').trim()) return '의류 사이즈를 입력해 주세요.'
      if (this.form.spec_type === 'PACK_COUNT' && (!detail.pack_unit_type_code || !Number.isSafeInteger(Number(detail.pack_count)) || Number(detail.pack_count) <= 0)) return '입수 코드와 양의 정수 입수량을 입력해 주세요.'
      if (this.target.expressMode && !(Number(this.form.weight_kg) > 0 && Number(this.form.weight_kg) <= 9999.99)) return '익스프레스 중량은 0보다 크고 9999.99kg 이하여야 합니다.'
      return ''
    },
    async save() {
      if (this.busy || !this.canSave || this.issue?.status === 'BLOCKED') return
      if (!this.isLink && !this.referenceLoaded) { this.error = '기준정보를 먼저 다시 조회해 주세요.'; return }
      if (!this.retryPayload && this.validateForm()) { this.error = this.validateForm(); return }
      this.busy = true
      this.error = ''
      // Keep the exact submitted payload after an uncertain response; a retry cannot overwrite newer data.
      this.retryPayload = this.retryPayload || this.buildPayload()
      try {
        await this.api('sku-save', this.retryPayload)
        this.changed = true
        this.retryPayload = null
      } catch (e) {
        const code = e?.response?.data?.code
        if (['VALIDATION', 'PACKING_SKU_RECHECK'].includes(code)) this.retryPayload = null
        this.error = this.errorMessage(e, '저장 결과를 확인할 수 없습니다. 입력과 주문 수량은 유지됩니다. 같은 내용으로 저장 재시도 또는 다시 조회하세요.')
        return
      } finally { this.busy = false }
      // A save response alone never resumes scanning: all order products must pass a fresh read.
      await this.check()
    },
    finish(ready) {
      const resolve = this.pending
      this.pending = null
      this.open = false
      this.queuedBarcode = ''
      this.$emit('active', false)
      if (resolve) resolve({ ready, changed: this.changed })
    }
  },
  beforeUnmount() { this.finish(false) }
}
</script>

<template>
  <q-dialog :model-value="open" persistent>
    <q-card style="width: 760px; max-width: 95vw">
      <q-card-section>
        <div class="text-h6">{{ title }}</div>
        <div class="text-caption text-grey-7">{{ target?.partnerLabel }} · 주문 {{ target?.orderNo }} · {{ issue?.barcode }}</div>
      </q-card-section>
      <q-separator />
      <q-card-section style="max-height: 65vh" class="scroll">
        <q-banner class="bg-amber-1 q-mb-md">상품 확인 중 추가 스캔은 접수하지 않습니다. 현재 주문 처리가 끝난 뒤 다시 스캔하세요.</q-banner>
        <div v-if="busy" class="row items-center q-gutter-sm q-mb-md"><q-spinner /><span>처리 중입니다.</span></div>
        <q-banner v-if="error" rounded class="bg-red-1 text-negative q-mb-md" role="alert">{{ error }}</q-banner>
        <q-banner v-if="!canSave && result" class="bg-amber-1 q-mb-md">공통 상품 등록·보완·연결은 관리자에게 요청해 주세요. 주문과 스캔 수량은 유지됩니다.</q-banner>
        <div v-if="stage === 'CONFIRM'">
          이 주문의 바코드에 해당하는 상품과 파트너 연결을 찾지 못했습니다. 신규 상품 등록창을 열겠습니까?
          <div class="text-caption q-mt-sm">확인만으로 저장되지 않습니다. 필수정보 입력 후 ‘상품 등록 저장’을 눌러야 저장됩니다.</div>
        </div>
        <div v-else-if="stage === 'LINK'">
          <div class="q-mb-md">기존 상품 ‘{{ form.display_name }}’의 현재 파트너 연결이 없습니다. 공통 상품을 새로 만들지 않고 연결합니다.</div>
          <q-input v-model="partnerSkuCode" label="파트너 상품 코드 (필수)" outlined dense maxlength="64" :disable="busy || !!retryPayload" />
        </div>
        <div v-else-if="stage === 'EDIT' && issue?.status !== 'BLOCKED'">
          <q-banner v-if="issue?.status === 'INCOMPLETE'" class="bg-amber-1 q-mb-md">기존 상품의 필수정보 또는 확정 상태를 보완합니다. 공통 상품 변경은 다른 파트너에도 적용됩니다.</q-banner>
          <fieldset :disabled="busy || !!retryPayload || !canSave" style="border: 0; padding: 0; margin: 0">
            <div class="row q-col-gutter-md">
              <q-input :model-value="issue?.barcode" label="바코드" outlined dense readonly class="col-6" />
              <q-input :model-value="target?.partnerLabel" label="현재 파트너" outlined dense readonly class="col-6" />
              <q-input v-model="form.display_name" label="상품명 (필수)" maxlength="255" outlined dense class="col-12" />
              <q-select v-model="form.brand_id" :options="brands" option-label="name" option-value="brand_id" label="브랜드 (필수)" emit-value map-options outlined dense class="col-6" />
              <q-select v-model="form.category_id" :options="categories" option-label="name" option-value="category_id" label="카테고리 (필수)" emit-value map-options outlined dense class="col-6" />
              <q-input v-model="form.hs_code" label="HS CODE (6자리 필수)" maxlength="6" outlined dense class="col-6" />
              <q-select v-model="form.spec_type" :options="specTypeOptions" label="규격 타입 (필수)" emit-value map-options outlined dense class="col-6" />
              <SpecTypeForm v-model="form.spec_detail" :spec-type="form.spec_type" class="col-12" />
              <div v-if="form.spec_type === 'PACK_COUNT'" class="col-12 text-caption">사용 가능한 입수 코드: {{ packUnits.map(item => item.code).join(', ') || '없음' }}</div>
              <q-input v-model="form.weight_kg" @update:model-value="onLogisticsValueInput(form, 'weight_kg')" :label="target?.expressMode ? '중량 kg (필수)' : '중량 kg'" type="number" min="0" step="0.001" outlined dense class="col-6" />
              <q-input v-model="form.volume_cbm" @update:model-value="onLogisticsValueInput(form, 'volume_cbm')" label="부피 cbm" type="number" min="0" step="0.000001" outlined dense class="col-6" />
              <q-input v-for="(label, key) in { length_cm: '가로 cm', width_cm: '세로 cm', height_cm: '높이 cm' }" :key="key" v-model="form[key]" :label="label" type="number" min="0" step="0.001" outlined dense class="col-4" />
              <LogisticsDefaultSuggestions class="col-12" :values="form" :disabled="busy || !!retryPayload || !canSave" @apply="applyDimensionDefaults(form)" />
              <q-input v-if="issue?.needsLink" v-model="partnerSkuCode" label="파트너 상품 코드 (필수)" maxlength="64" outlined dense class="col-12" />
            </div>
          </fieldset>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn flat label="취소 · 포장 유지" :disable="busy" @click="finish(false)" />
        <q-btn flat label="다시 조회" :disable="busy" @click="check" />
        <q-btn v-if="stage === 'CONFIRM' && canSave" color="primary" label="등록창 열기" :disable="busy" @click="confirmRegistration" />
        <q-btn v-if="['EDIT', 'LINK'].includes(stage) && canSave && issue?.status !== 'BLOCKED'" color="primary" :disable="busy" :label="retryPayload ? '같은 내용으로 저장 재시도' : isLink ? '기존 상품 연결 저장' : issue?.status === 'MISSING' ? '상품 등록 저장' : '필수정보 보완 저장'" @click="save" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
